/**
 * Lightweight route keys for the browser router.
 *
 * Do not import the full specialty/audience registries into App.tsx: those
 * modules contain all page copy and capture metadata, and doing so puts that
 * content in the initial client bundle before the matching lazy page loads.
 * verify-client-routes.mjs compares these lists with the authoritative data
 * registries on every production build.
 *
 * The two live slugs below therefore carry `specialty-literal-allow`, and the
 * reason is the paragraph above: reaching the registry for them is the one
 * thing this module exists to avoid. They are also URL segments (a row's
 * `slug`), not the stored `specialty_id` the guard is protecting — the registry
 * keeps those as separate fields — and they cannot drift unnoticed: `npm run
 * build` runs verify:client-routes BEFORE vite, and it throws if these lists
 * stop matching LIVE_SPECIALTIES / COMING_SOON_SPECIALTIES / AUDIENCES.
 */
export const LIVE_SPECIALTY_ROUTE_SLUGS = [
  "anesthesia", // specialty-literal-allow — see header
  "neurosurgery", // specialty-literal-allow — see header
] as const;

export const COMING_SOON_SPECIALTY_ROUTE_SLUGS = [
  "ent",
  "general-surgery",
  "orthopaedic-surgery",
  "plastic-surgery",
  "urology",
  "vascular-surgery",
  "cardiothoracic-surgery",
  "ophthalmology",
  "obgyn",
  "interventional-radiology",
  "omfs",
] as const;

export const AUDIENCE_ROUTE_SLUGS = [
  "medical-students",
  "residents",
  "surgical-residents",
  "fellowship",
  "co-residents",
] as const;

/** The comparison pages (src/data/compare.ts), each served at `/compare/<slug>`. */
export const COMPARE_ROUTE_SLUGS = [
  "acgme-case-log-apps",
  "anesthesia-case-log-apps",
  "surgical-case-log-apps",
  "medical-student-procedure-log",
] as const;

/**
 * The LIVE advertorials (src/data/advertorials.ts, D-229), each served at
 * `/read/<slug>`. A slug here is the ONLY switch that takes one live: the
 * registry derives its live set from this list, so the two cannot disagree. A
 * slug that is not here is a draft, rendered by the dev server alone. Empty
 * while every advertorial waits for the owner's read.
 */
export const LIVE_ADVERTORIAL_ROUTE_SLUGS: readonly string[] = [];

/**
 * The ACGME case-minimums library (E-149), each served at
 * `/acgme-case-minimums/<slug>`. Re-exported from the generated route keys
 * rather than listed here: the generated module holds nothing but the keys, and
 * fifty hand-typed slugs is exactly the drift verify-client-routes exists for.
 */
export { ACGME_MINIMUMS_SLUGS as ACGME_MINIMUMS_ROUTE_SLUGS } from "./generated/acgme-routes.ts";
